<script lang="ts">
	import type { Navigation } from '$models/Navigation/Navigation';
	import { tick } from 'svelte';
	import { getMobileMenuState } from '$lib/state/menu.svelte';

	let { navigation }: { navigation: Navigation } = $props();

	const mobileMenuState = getMobileMenuState();

	let openItemId: number | null = $state(null);
	let visibleItemId: number | null = $state(null);
	let flippedItemId: number | null = $state(null);
	const submenuEls: Record<number, HTMLUListElement> = {};

	function resetMenu() {
		openItemId = null;
		visibleItemId = null;
		flippedItemId = null;
	}

	async function toggleItem(id: number, event: MouseEvent) {
		event.preventDefault();

		if (openItemId === id) {
			resetMenu();
			return;
		}

		openItemId = id;
		visibleItemId = null;
		flippedItemId = null;

		await tick();

		const submenuEl = submenuEls[id];
		const liEl = submenuEl?.parentElement;

		if (submenuEl && liEl) {
			const width = submenuEl.offsetWidth;
			const liRect = liEl.getBoundingClientRect();
			const defaultLeft = liRect.left + liRect.width / 2 - width * 0.3;
			const defaultRight = defaultLeft + width;

			if (defaultRight > document.documentElement.clientWidth - 16) {
				flippedItemId = id;
			}
		}

		visibleItemId = id;
	}

	function closeMenu() {
		resetMenu();
		mobileMenuState.setActive(false);
	}

	function handleWindowClick(event: MouseEvent) {
		if (openItemId === null) return;

		const target = event.target as HTMLElement;
		if (!target.closest('.header-menu')) {
			resetMenu();
		}
	}

	function handleWindowKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			resetMenu();
		}
	}

	function handleWindowResize() {
		resetMenu();
	}
</script>

<svelte:window
	onclick={handleWindowClick}
	onkeydown={handleWindowKeydown}
	onresize={handleWindowResize}
/>

{#snippet chevrondDown()}
	<svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M5 6.0625L0 1.0625L1.0625 0L5 3.9375L8.9375 0L10 1.0625L5 6.0625Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

{#snippet arrowRight()}
	<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 8L6.9375 6.9375L9.125 4.75H0V3.25H9.125L6.9375 1.0625L8 0L12 4L8 8Z"
			fill="currentColor"
		/>
	</svg>
{/snippet}

<ul>
	{#each navigation.items as item}
		{@const hasChildren = item.children && item.children.length}
		{#if !item.parent}
			<li class:has-children={hasChildren} class:is-open={openItemId === item.id}>
				<a
					href={item.url}
					class={item.classes.join(' ')}
					target={item.target ?? '_self'}
					aria-expanded={hasChildren ? openItemId === item.id : undefined}
					onclick={(event) => (hasChildren ? toggleItem(item.id, event) : closeMenu())}
				>
					{item.title}
					{#if hasChildren}
						<span class="menu-link-icon">{@render chevrondDown()}</span>
					{/if}
				</a>

				{#if hasChildren}
					<ul
						bind:this={submenuEls[item.id]}
						class:is-flipped={flippedItemId === item.id}
						class:is-visible={visibleItemId === item.id}
					>
						{#each item.children as child}
							<li>
								<a
									href={child.url}
									class={child.classes.join(' ')}
									target={child.target ?? '_self'}
									onclick={closeMenu}
								>
									{child.title}
									<span class="menu-sub-link-icon">{@render arrowRight()}</span>
								</a>
							</li>
						{/each}
					</ul>
				{/if}
			</li>
		{/if}
	{/each}
</ul>
